import { Box, Group, Stack, Text, Tooltip } from '@mantine/core';
import dayjs from 'dayjs';
import type { DashboardRow } from '../api-client.ts';
import { tokens, type ReservationStatus } from '../ui/tokens.ts';

const STATUS_LABEL: Record<ReservationStatus, string> = {
  pending: '待安排',
  confirmed: '已確認',
  checked_in: '客人到場',
  completed: '已完成',
  canceled: '已取消',
  no_show: '未出現',
};

interface TimelineViewProps {
  rows: readonly DashboardRow[];
  windowStartIso: string;
  windowEndIso: string;
  timezone: string;
  customerLabels: Map<string, { label: string; emoji: string }>;
}

/**
 * Horizontal timeline of reservations across the rolling 24h window.
 * Each row is a colour-coded block positioned by time; overlapping
 * reservations stack on separate lanes (greedy first-fit).
 */
export function TimelineView(props: TimelineViewProps) {
  const windowStart = Date.parse(props.windowStartIso);
  const windowEnd = Date.parse(props.windowEndIso);
  const windowMs = windowEnd - windowStart;

  // Greedy lane assignment: each new reservation goes in the first lane
  // whose last end-time is <= its start-time.
  const sorted = [...props.rows].sort(
    (a, b) => Date.parse(a.startTime) - Date.parse(b.startTime),
  );
  const lanes: { reservation: DashboardRow; endMs: number }[][] = [];
  const laneOf = new Map<string, number>();
  for (const r of sorted) {
    const sMs = Date.parse(r.startTime);
    const eMs = Date.parse(r.endTime);
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      const lane = lanes[i]!;
      const last = lane[lane.length - 1];
      if (last && last.endMs <= sMs) {
        lane.push({ reservation: r, endMs: eMs });
        laneOf.set(r.reservationId, i);
        placed = true;
        break;
      }
    }
    if (!placed) {
      lanes.push([{ reservation: r, endMs: eMs }]);
      laneOf.set(r.reservationId, lanes.length - 1);
    }
  }

  const laneCount = Math.max(1, lanes.length);
  const LANE_H = 32;
  const GAP = 4;

  // Hour ticks every 4h from windowStart.
  const tickCount = 7;
  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const t = windowStart + (windowMs * i) / (tickCount - 1);
    return {
      pct: (i / (tickCount - 1)) * 100,
      label: dayjs(t).tz(props.timezone).format('HH:mm'),
    };
  });

  return (
    <Box
      style={{
        padding: 16,
        background: tokens.color.cardBackground,
        borderRadius: tokens.radius.lg,
        border: `1px solid ${tokens.color.cardBorder}`,
      }}
    >
      <Group justify="space-between" mb="sm">
        <Text fw={600}>時間軸</Text>
        <Text size="xs" c="dimmed">
          {dayjs(windowStart).tz(props.timezone).format('MM/DD HH:mm')} →{' '}
          {dayjs(windowEnd).tz(props.timezone).format('MM/DD HH:mm')}
        </Text>
      </Group>

      {props.rows.length === 0 ? (
        <Text c="dimmed" size="sm" ta="center" py="md">
          此時段沒有預約
        </Text>
      ) : (
        <Stack gap={6}>
          {/* Ticks row */}
          <Box style={{ position: 'relative', height: 18 }}>
            {ticks.map((t, i) => (
              <Box
                key={i}
                style={{
                  position: 'absolute',
                  left: `${t.pct}%`,
                  transform: 'translateX(-50%)',
                  fontSize: 11,
                  color: tokens.color.ink + '99',
                }}
              >
                {t.label}
              </Box>
            ))}
          </Box>

          {/* Lane canvas */}
          <Box
            style={{
              position: 'relative',
              height: laneCount * (LANE_H + GAP),
              background:
                'linear-gradient(to right, transparent 0, transparent calc(100% / 6 - 1px), ' +
                tokens.color.cardBorder +
                ' calc(100% / 6 - 1px), ' +
                tokens.color.cardBorder +
                ' calc(100% / 6), transparent calc(100% / 6))',
              backgroundSize: 'calc(100% / 6) 100%',
              borderRadius: tokens.radius.md,
            }}
          >
            {sorted.map((r) => {
              const sMs = Date.parse(r.startTime);
              const eMs = Date.parse(r.endTime);
              const lane = laneOf.get(r.reservationId) ?? 0;
              const leftPct = ((sMs - windowStart) / windowMs) * 100;
              const widthPct = ((eMs - sMs) / windowMs) * 100;
              if (widthPct <= 0 || leftPct >= 100) return null;
              const info = props.customerLabels.get(r.reservationId) ?? {
                label: '—',
                emoji: '✨',
              };
              const statusCfg = tokens.status[r.status as ReservationStatus];
              return (
                <Tooltip
                  key={r.reservationId}
                  label={
                    <Stack gap={2}>
                      <Text size="xs" fw={600}>
                        {info.emoji} {info.label} · {r.partySize} 位
                      </Text>
                      <Text size="xs">
                        {dayjs(sMs).tz(props.timezone).format('HH:mm')} →{' '}
                        {dayjs(eMs).tz(props.timezone).format('HH:mm')}
                      </Text>
                      <Text size="xs">{STATUS_LABEL[r.status as ReservationStatus]}</Text>
                    </Stack>
                  }
                  withArrow
                >
                  <Box
                    style={{
                      position: 'absolute',
                      left: `${Math.max(0, leftPct)}%`,
                      width: `${Math.min(widthPct, 100 - Math.max(0, leftPct))}%`,
                      top: lane * (LANE_H + GAP),
                      height: LANE_H,
                      background: statusCfg.accent,
                      color: tokens.color.white,
                      borderRadius: tokens.radius.md,
                      padding: '4px 8px',
                      fontSize: 12,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'default',
                    }}
                  >
                    {info.emoji} {info.label} · {r.partySize}人
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Stack>
      )}
    </Box>
  );
}
